import { useState, useCallback, useEffect } from 'react';
import {
    Modal, Steps, Upload, Table, Form, Input, Select, Switch, Button,
    message, Alert, Progress, Space, Typography, Tag, App
} from 'antd';
import { InboxOutlined, CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, MailOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import { useSuperAdmins } from '../../hooks/use_super_admins';
import { useAuth } from '../../context/AuthContext';

const { Dragger } = Upload;
const { Text } = Typography;

const EXAMPLE_CSV = [
    'name,email,description',
    '"Ada Lovelace",ada@example.com,"Mathematician and writer"',
    '"Alan Turing",alan@example.com,"Computer scientist"',
].join('\n');

function downloadExampleCSV() {
    const blob = new Blob([EXAMPLE_CSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'example-import.csv';
    link.click();
    URL.revokeObjectURL(url);
}

const GROUP_ENTITY_TYPES = [
    { value: 'context', label: 'Context' },
    { value: 'relationship', label: 'Relationship' },
];

// Simple CSV parser that handles quoted fields and multiple line endings
function parseCSV(text) {
    const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n');
    const result = [];

    function parseLine(line) {
        const fields = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (ch === ',' && !inQuotes) {
                fields.push(current.trim());
                current = '';
            } else {
                current += ch;
            }
        }
        fields.push(current.trim());
        return fields;
    }

    if (lines.length < 2) return { headers: [], rows: [] };

    const headers = parseLine(lines[0]).map(h => h.toLowerCase());

    for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = parseLine(lines[i]);
        const row = {};
        headers.forEach((h, idx) => {
            row[h] = values[idx] || '';
        });
        result.push(row);
    }

    return { headers, rows: result };
}

function extractPeople(rows, headers) {
    // Find name column (flexible matching)
    const nameKey = headers.find(h => h === 'name' || h === 'full name' || h === 'fullname');
    // Find description column
    const descKey = headers.find(
        h => h === 'description' || h === 'bio' || h === 'about' || h === 'summary' || h === 'notes'
    );
    // Find email column — needed to later send a claim link (person's entity page)
    const emailKey = headers.find(h => h === 'email' || h === 'e-mail' || h === 'email address');

    return rows.map((row, i) => ({
        key: i,
        name: nameKey ? row[nameKey] : '',
        description: descKey ? row[descKey] : '',
        email: emailKey ? row[emailKey] : '',
        raw: row,
    })).filter(p => p.name.trim());
}

async function runWithConcurrency(tasks, limit) {
    let index = 0;
    async function runNext() {
        if (index >= tasks.length) return;
        const i = index++;
        await tasks[i]();
        await runNext();
    }
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, runNext));
}

function CSVImportModal({ open, onCancel, onSuccess }) {
    const { modal } = App.useApp();
    const api = useApi();
    const { featureEntityListing, enabledEntityTypes, connectionsEnabled } = useNodeInfo();
    // Only offer group types the node has enabled (null = not yet known, allow all).
    // connectionsEnabled is false when neither is enabled, which hides the whole
    // group section — there would be nothing to connect people to.
    const groupTypeOptions = enabledEntityTypes
        ? GROUP_ENTITY_TYPES.filter(t => enabledEntityTypes.includes(t.value))
        : GROUP_ENTITY_TYPES;
    const [currentStep, setCurrentStep] = useState(0);
    const [people, setPeople] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [form] = Form.useForm();
    const createGroup = Form.useWatch('createGroup', form);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState(null); // { created, failed, groupEntity }
    // Importing never emails anyone. Once it's done a superadmin can choose to
    // invite the imported people who have an email — the same claim-link send
    // as "Send email invite" on a person's page, in this node's default
    // wording (Node settings → Invite email). claim-link is superadmin-only.
    const { user } = useAuth();
    const { isServerAdmin } = useSuperAdmins();
    const [invites, setInvites] = useState(null); // { sending, sent, notSent, failed: [{ name, reason }] }

    // The initial groupType ('context') may be disabled on this node once /info
    // resolves; fall back to the first enabled group type.
    const groupType = Form.useWatch('groupType', form);
    useEffect(() => {
        if (groupTypeOptions.length && !groupTypeOptions.some(t => t.value === groupType)) {
            form.setFieldValue('groupType', groupTypeOptions[0].value);
        }
    }, [groupTypeOptions, groupType, form]);

    const handleFileUpload = useCallback((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            const { headers: parsedHeaders, rows } = parseCSV(text);
            const extracted = extractPeople(rows, parsedHeaders);

            if (extracted.length === 0) {
                message.error('No people found. Make sure your CSV has a "name" column.');
                return;
            }

            setHeaders(parsedHeaders);
            setPeople(extracted);

            // Autofill group name from filename (strip .csv extension, replace separators)
            const baseName = file.name.replace(/\.csv$/i, '').replace(/[_-]/g, ' ');
            const groupName = baseName.charAt(0).toUpperCase() + baseName.slice(1);

            // Autofill group description from a context/description column if present
            const descKey = parsedHeaders.find(
                h => h === 'context' || h === 'context name' || h === 'context description' || h === 'group description'
            );
            const groupDescription = descKey ? (rows[0]?.[descKey] || '') : '';

            form.setFieldsValue({ groupName, groupDescription });

            setCurrentStep(1);
        };
        reader.readAsText(file);
        return false; // prevent antd auto-upload
    }, [form]);

    const handleImport = async (values) => {
        setProcessing(true);
        setCurrentStep(2);
        setProgress(0);

        const created = [];
        const failed = [];
        // The switch is hidden when no group type is enabled, but its initial
        // value is still true — gate it here so nothing gets created behind it.
        const makeGroup = connectionsEnabled && !!values.createGroup;
        // Gated off => the node has no listed/unlisted concept, so the field is
        // absent from the form and the backend ignores is_listed on create
        // anyway; send false rather than an undefined from a hidden field.
        const isListed = featureEntityListing && !!values.isListed;
        // Each person = 1 create (+ 1 connect once a group entity exists); plus 1
        // group create when makeGroup is on.
        const total = people.length * (makeGroup ? 2 : 1) + (makeGroup ? 1 : 0);
        let done = 0;
        const tick = () => { done++; setProgress(Math.round((done / total) * 100)); };

        // Step A: create the group entity first — skipped entirely when the user
        // turns "Group entity" off, so people are added directly with no connections.
        let groupEntity = null;
        if (makeGroup) {
            try {
                groupEntity = await api.post('/api/entities', {
                    name: values.groupName,
                    description: values.groupDescription || null,
                    entity_type: values.groupType,
                    is_listed: isListed,
                });
            } catch (err) {
                message.error(`Could not create group entity: ${err.message}`);
                setProcessing(false);
                return;
            }
            tick();
        }

        // Step C: for each person — create, then (if a group entity exists) immediately
        // link to it (5 at a time)
        let connectedCount = 0;
        const CONCURRENCY = 5;
        const tasks = people.map(person => async () => {
            let personEntity = null;
            try {
                personEntity = await api.post('/api/entities', {
                    name: person.name,
                    description: person.description || null,
                    email: person.email || null,
                    entity_type: 'person',
                    is_listed: isListed,
                });
                created.push(personEntity);
            } catch (err) {
                failed.push({ name: person.name, reason: err.message });
                tick();
                if (groupEntity) tick(); // skip connect tick too
                return;
            }
            tick();

            if (groupEntity) {
                // Immediately connect this person to the group entity
                try {
                    const a = Math.min(personEntity.id, groupEntity.id);
                    const b = Math.max(personEntity.id, groupEntity.id);
                    await api.post('/api/connections', { entity_a_id: a, entity_b_id: b, connected_status: true });
                    connectedCount++;
                } catch {
                    // connection failure is non-fatal, continue
                }
                tick();
            }
        });
        await runWithConcurrency(tasks, CONCURRENCY);

        setResults({ created, failed, groupEntity, connectedCount });
        setProcessing(false);
        setProgress(100);

        if (onSuccess) onSuccess();
    };

    const invitable = (results?.created || []).filter(e => e.email);

    const sendInvites = async () => {
        const state = { sending: true, sent: 0, notSent: 0, failed: [] };
        setInvites({ ...state });
        // Fewer at a time than the import: each one is an SMTP send.
        await runWithConcurrency(invitable.map(entity => async () => {
            try {
                const link = await api.post(`/api/entities/${entity.id}/claim-link`, undefined, {
                    params: { user_id: user?.id, send_email: true },
                });
                // email_sent is false when the node has no SMTP configured —
                // the link exists, it just went nowhere.
                if (link.email_sent) state.sent++; else state.notSent++;
            } catch (err) {
                state.failed.push({ name: entity.name, reason: err.message });
            }
            setInvites({ ...state });
        }), 3);
        setInvites({ ...state, sending: false });
    };

    const resetState = () => {
        setInvites(null);
        setCurrentStep(0);
        setPeople([]);
        setHeaders([]);
        form.resetFields();
        setProcessing(false);
        setProgress(0);
        setResults(null);
    };

    const handleClose = () => {
        if (invites?.sending) return; // a few seconds at most; don't strand half the sends
        if (processing) {
            modal.confirm({
                title: 'Import in progress',
                content: 'Closing now will stop the import. Entities already created will remain linked. Are you sure you want to stop?',
                okText: 'Stop import',
                okButtonProps: { danger: true },
                cancelText: 'Continue importing',
                onOk: () => { resetState(); if (onCancel) onCancel(); },
            });
            return;
        }
        resetState();
        if (onCancel) onCancel();
    };

    const previewColumns = [
        { title: 'Name', dataIndex: 'name', key: 'name', width: 180 },
        { title: 'Email', dataIndex: 'email', key: 'email', width: 200, ellipsis: true },
        { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true },
    ];

    const stepItems = [
        { title: 'Upload CSV' },
        { title: 'Preview & Configure' },
        { title: 'Import' },
    ];

    return (
        <Modal
            title="Import People from CSV"
            open={open}
            onCancel={handleClose}
            maskClosable={!processing && !invites?.sending}
            keyboard={!processing && !invites?.sending}
            footer={null}
            width={680}
        >
            <Steps
                current={currentStep}
                items={stepItems}
                style={{ marginBottom: 24 }}
                size="small"
            />

            {/* Step 0: Upload */}
            {currentStep === 0 && (
                <div>
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Expected CSV format"
                        description={
                            <div>
                                <div style={{ marginBottom: 8 }}>
                                    First row must be a header. One row per person:
                                </div>
                                <ul style={{ margin: 0, paddingLeft: 20 }}>
                                    <li><Tag color="blue">name</Tag> required</li>
                                    <li><Tag>email</Tag> optional — lets you invite them once the import is done</li>
                                    <li><Tag>description</Tag> optional</li>
                                </ul>
                                <div
                                    style={{
                                        marginTop: 12,
                                        padding: '8px 10px',
                                        fontFamily: 'monospace',
                                        fontSize: 12,
                                        background: 'var(--bg-secondary)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 4,
                                        whiteSpace: 'pre',
                                        overflowX: 'auto',
                                    }}
                                >
                                    {EXAMPLE_CSV}
                                </div>
                                <Button
                                    type="link"
                                    icon={<DownloadOutlined />}
                                    onClick={downloadExampleCSV}
                                    style={{ padding: '8px 0 0' }}
                                >
                                    Download example CSV
                                </Button>
                            </div>
                        }
                    />
                    <Dragger
                        accept=".csv"
                        beforeUpload={handleFileUpload}
                        showUploadList={false}
                        style={{ padding: '12px 0' }}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag a CSV file here</p>
                    </Dragger>
                </div>
            )}

            {/* Step 1: Preview + Configure */}
            {currentStep === 1 && (
                <div>
                    <Alert
                        message={`${people.length} people found in CSV`}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Table
                        dataSource={people}
                        columns={previewColumns}
                        size="small"
                        pagination={{ pageSize: 5 }}
                        style={{ marginBottom: 24 }}
                    />

                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleImport}
                        initialValues={{ createGroup: true, groupType: 'context', isListed: true }}
                    >
                        {connectionsEnabled && (
                            <Form.Item
                                label="Group entity"
                                name="createGroup"
                                valuePropName="checked"
                                tooltip="Creates a group entity and connects every imported person to it. Turn off to add people directly with no connections."
                            >
                                <Switch checkedChildren="On" unCheckedChildren="Off" />
                            </Form.Item>
                        )}
                        {featureEntityListing && (
                            <Form.Item
                                name="isListed"
                                valuePropName="checked"
                                label="Listed"
                                tooltip="Listed entities appear in public/discoverable views and search. Unlisted entities are only visible to people who already know them."
                            >
                                <Switch checkedChildren="Listed" unCheckedChildren="Unlisted" />
                            </Form.Item>
                        )}
                        {connectionsEnabled && createGroup && (
                            <>
                                <Form.Item
                                    label="Group Entity Name"
                                    name="groupName"
                                    rules={[{ required: true, message: 'Please enter a name' }]}
                                >
                                    <Input placeholder="e.g. Team Alpha, Cohort 2024..." />
                                </Form.Item>
                                <Form.Item label="Group Entity Description" name="groupDescription">
                                    <Input.TextArea rows={2} placeholder="Optional description" />
                                </Form.Item>
                                <Form.Item label="Group Entity Type" name="groupType">
                                    <Select options={groupTypeOptions} />
                                </Form.Item>
                            </>
                        )}
                        <Form.Item>
                            <Space>
                                <Button onClick={() => setCurrentStep(0)}>Back</Button>
                                <Button type="primary" htmlType="submit">
                                    Import {people.length} people
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </div>
            )}

            {/* Step 2: Processing / Results */}
            {currentStep === 2 && (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    {processing ? (
                        <>
                            <Progress percent={progress} status="active" />
                            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
                                Creating entities and connections…
                            </Text>
                        </>
                    ) : results && (
                        <div style={{ textAlign: 'left' }}>
                            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                <Alert
                                    message={`Import complete`}
                                    description={
                                        <>
                                            <div><CheckCircleOutlined style={{ color: 'green' }} /> {results.created.length} person entities created</div>
                                            {results.failed.length > 0 && (
                                                <div><CloseCircleOutlined style={{ color: 'red' }} /> {results.failed.length} failed</div>
                                            )}
                                            {results.groupEntity ? (
                                                <>
                                                    <div><CheckCircleOutlined style={{ color: 'green' }} /> Group entity <Tag>{results.groupEntity.name}</Tag> [{results.groupEntity.entity_type}] created and linked to this entity</div>
                                                    <div><CheckCircleOutlined style={{ color: 'green' }} /> {results.connectedCount} connections established</div>
                                                </>
                                            ) : (
                                                <div><CheckCircleOutlined style={{ color: 'green' }} /> Added directly to this node — no connections created</div>
                                            )}
                                        </>
                                    }
                                    type={results.failed.length === 0 ? 'success' : 'warning'}
                                    showIcon
                                />
                                {results.failed.length > 0 && (
                                    <Alert
                                        message="Some rows could not be imported"
                                        description={results.failed.map(f => `${f.name}: ${f.reason}`).join('\n')}
                                        type="error"
                                    />
                                )}
                                {isServerAdmin && invitable.length > 0 && (
                                    <Alert
                                        type={invites && !invites.sending && invites.failed.length ? 'warning' : 'info'}
                                        showIcon
                                        icon={<MailOutlined />}
                                        message={!invites
                                            ? `${invitable.length} of them have an email address`
                                            : invites.sending
                                                ? `Sending invites… ${invites.sent + invites.notSent + invites.failed.length} of ${invitable.length}`
                                                : 'Invites sent'}
                                        description={!invites ? (
                                            <Space direction="vertical" size="small">
                                                <Text>
                                                    Nobody has been emailed. Send each of them an invitation to
                                                    claim their profile, using this node&apos;s invite wording?
                                                </Text>
                                                <Button type="primary" icon={<MailOutlined />} onClick={sendInvites}>
                                                    Email invites to {invitable.length} {invitable.length === 1 ? 'person' : 'people'}
                                                </Button>
                                            </Space>
                                        ) : invites.sending ? (
                                            <Progress
                                                percent={Math.round(((invites.sent + invites.notSent + invites.failed.length) / invitable.length) * 100)}
                                                status="active"
                                            />
                                        ) : (
                                            <>
                                                {invites.sent > 0 && (
                                                    <div><CheckCircleOutlined style={{ color: 'green' }} /> {invites.sent} invites emailed</div>
                                                )}
                                                {invites.notSent > 0 && (
                                                    <div>
                                                        <CloseCircleOutlined style={{ color: 'red' }} /> {invites.notSent} links created but not
                                                        emailed — email isn&apos;t set up on this node. Copy each link from the person&apos;s page.
                                                    </div>
                                                )}
                                                {invites.failed.map(f => (
                                                    <div key={f.name}><CloseCircleOutlined style={{ color: 'red' }} /> {f.name}: {f.reason}</div>
                                                ))}
                                            </>
                                        )}
                                    />
                                )}
                                <Button
                                    type={isServerAdmin && invitable.length > 0 && !invites ? 'default' : 'primary'}
                                    onClick={handleClose}
                                    disabled={invites?.sending}
                                >
                                    Done
                                </Button>
                            </Space>
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}

function CSVImportModalWrapper(props) {
    return <App><CSVImportModal {...props} /></App>;
}

export default CSVImportModalWrapper;
